<template>
  <base-modal
    :title="$t('modal.enterQueryString.title')"
    :confirmButton="$t('modal.enterQueryString.confirmBtn')"
    :confirmEnabled="!(model === null || model === '' || !validateQueryString.valid)"
    @close="$emit('close')"
    @confirmed="$emit('confirmed', [model, querySyntaxMethod])"
  >
    <div slot="body">
      <p>{{ $t('modal.enterQueryString.subtitle') }}</p>
      <p class="query-example">{{ $t('misc.exampleQuery') }}</p>
      <v-text-field
        v-model="model"
        :label="$t('modal.enterQueryString.queryInputLabel')"
        textarea
        rows="5"
        multi-line
        hide-details
      ></v-text-field>
      <p v-if="!validateQueryString.valid" class="error-message">{{ validateQueryString.error }}</p>
      <div class="subtitle">
        <p class="subtitle-text">{{ $t('modal.enterQueryString.querySyntaxTitle') }}</p>
        <v-tooltip right content-class="query-syntax-tooltip">
          <span slot="activator" style="display: flex;">
            <v-icon slot="activator" class="subtitle-icon material-symbols-outlined">help</v-icon>
          </span>
          <span >{{ $t('modal.enterQueryString.querySyntaxTooltip') }}</span>
        </v-tooltip>
      </div>
      <v-btn-toggle
        mandatory
        v-model="querySyntaxMethod"
        class="query-syntax-btn-group"
        color="info"
      >
        <v-btn flat value="polyglot" :color="querySyntaxMethod==='polyglot' ? 'primary' : ''">
          <span>{{ $t('misc.primarySyntaxMethod') }}</span>
        </v-btn>
        <v-btn flat value="web" :color="querySyntaxMethod==='web' ? 'primary' : ''">
          <span>{{ $t('misc.secondarySyntaxMethod') }}</span>
        </v-btn>
      </v-btn-toggle>
      <p class="hint-message">
        <i18n path="modal.enterQueryString.helpMessage.text" tag="p">
          <template v-slot:editUrl>
            <a :href="editSearchVideoUrl" target="_blank">{{ $t('modal.enterQueryString.helpMessage.editUrl')}}</a>
            <v-icon>open_in_new</v-icon>
          </template>
          <template v-slot:buildCanvasUrl>
            <a :href="buildOnCanvasVideoUrl" target="_blank">{{ $t('modal.enterQueryString.helpMessage.buildCanvasUrl')}}</a>
            <v-icon>open_in_new</v-icon>
          </template>
        </i18n>
      </p>
    </div>
  </base-modal>
</template>
<script>
import '../../style/_variables.scss'
import { editSearchVideoUrl, buildOnCanvasVideoUrl } from '../../utils/constants'

export default {
  name: 'enter-query-string',
  props: ['model'],
  computed: {
    validateQueryString () {
      if (!this.model) return { valid: true, error: '' }
      const input = this.model
      const stack = []
      const brackets = {
        '(': ')',
        '[': ']',
        '{': '}'
      }

      // Helper function to get context for error message
      const getContext = (position, isOpening) => {
        if (isOpening) {
          // For missing opening bracket, show context after the closing bracket
          return '...' + input.slice(Math.max(0, position - 3), position + 1)
        } else {
          // For missing closing bracket, show context before the orphaned opening bracket
          return input.slice(position, Math.min(position + 20, input.length)) + '...'
        }
      }

      // Keep track of the last opening bracket's position
      let lastOpeningPos = -1

      // Iterate through each character
      for (let i = 0; i < input.length; i++) {
        const char = input[i]

        // If it's an opening bracket
        if (Object.keys(brackets).includes(char)) {
          stack.push({ char, position: i })
          lastOpeningPos = i
        } else if (Object.values(brackets).includes(char)) {
          // If stack is empty, we found a closing bracket without an opening one
          if (stack.length === 0) {
            return {
              valid: false,
              error: `Missing starting brackets before "${getContext(i, true)}"`,
              position: i
            }
          }

          const lastOpening = stack.pop()

          // Check for empty pair of brackets
          if (i - lastOpeningPos === 1) {
            return {
              valid: false,
              error: `Empty pair of brackets at "${getContext(lastOpeningPos, false)}"`,
              position: lastOpeningPos
            }
          }

          // Check if brackets match
          if (brackets[lastOpening.char] !== char) {
            return {
              valid: false,
              error: `Mismatched brackets: expected ${brackets[lastOpening.char]} but found ${char}`,
              position: i
            }
          }

          // Update lastOpeningPos to the previous opening bracket in the stack
          lastOpeningPos = stack.length > 0 ? stack[stack.length - 1].position : -1
        }
      }

      // If we still have items in the stack, we have unclosed brackets
      if (stack.length > 0) {
        const lastUnclosed = stack[stack.length - 1]
        return {
          valid: false,
          error: `Unclosed brackets after "${getContext(lastUnclosed.position, false)}"`,
          position: lastUnclosed.position
        }
      }

      // If we get here, all brackets are properly paired
      return {
        valid: true,
        error: "",
        position: null
      }
    }
  },
  components: {
    BaseModal: () => import('../modals/BaseModal')
  },
  data () {
    return {
      queryString: '',
      querySyntaxMethod: 'polyglot',
      editSearchVideoUrl,
      buildOnCanvasVideoUrl
    }
  }
}
</script>
<style scoped lang="scss">
p {
  margin: 0;
  text-align: left;
  font-size: $length-med-s;
  color: $grey-800;
  &.query-example {
    padding-left: $length-small-l;
    color: $grey-700
  }
}
.error-message {
  color: red;
  font-size: $font-size-small;
  margin-top: 4px;
  text-align: left;
}
.subtitle {
  padding-top: 15px;
  display: flex;
  &-text {
    color: black;
    font-size: $font-size-large;
  }
  &-icon {
    padding: $length-small-m;
  }
}
.query-syntax-btn-group {
  width: $length-xlarge-m;
}
.btn-toggle .btn:first-child {
  border-radius: 12px 0 0 12px;
}
.btn-toggle .btn:last-child {
  border-radius: 0 12px 12px 0;
}
.second-btn > .btn__content {
  border-radius: 0 12px 12px 0;
}
.query-syntax {
  &-tooltip > span {
    display: block;
    text-align: left;
    white-space: pre-wrap;
    font-size: $font-size-small;
  }
  &-btn-group {
    display: flex;
    width: fit-content;
    border-radius: $length-med-xs;
  }
  &-btn-active {
    color: blue
  }
}
.btn {
  background-color: $grey-400;
  min-width: $length-xlarge-l;
  min-height: $length-large-xs;
  border-radius: $length-med-xs;
  text-transform: capitalize;
  font-size: $font-size-med;
  color: $grey-800;
}
.hint-message {
  padding: $length-med-l 0 $length-med-xs 0;
}
</style>



// WEBPACK FOOTER //
// src/components/mainMenu/EnterQueryString.vue